import { useState, useCallback } from "react";
import { getSpeedApprovalsAPI, approveSpeedInAppAPI } from "@services/liveTrakAPI";
import { useUser } from "@contexts/UserContext";
import { useError } from "@contexts/ErrorContext";
import { useLoading } from "@contexts/LoadingContext";
import { useSuccess } from "@contexts/SuccessContext";

/**
 * Holds speed approval state and provides approval/rejection functions to work alongside the SpeedApproval table
 * 
 * @param {DropdownOption} selectedJobOption 
 * @param {DropdownOption} selectedRunOption 
 */
export const useSpeedApprovals = (selectedJobOption, selectedRunOption) => {
  const { updatePendingSpeedApprovals } = useUser();
  const { showError } = useError();
  const {timeoutSuccess} = useSuccess();
  const {hideLoading, showLoading} = useLoading();

  const [speedApprovals, setSpeedApprovals] = useState([]);

  // Fetch all pending speed approvals for the user
  const fetchSpeedApprovals = useCallback(async (UserID = null) => {
    if (!selectedJobOption?.value) return;

    try {
      const response = await getSpeedApprovalsAPI(
        selectedJobOption.value,
        selectedRunOption?.value,
        UserID
      );

      setSpeedApprovals(response.data?.speeedApprovals || []);
      updatePendingSpeedApprovals(null, false)

    } catch (err) {
      console.error(err);
      showError("Failed to load approvals");
    }
  }, [selectedJobOption, selectedRunOption, showError, updatePendingSpeedApprovals]);

  // Approve a speed
  const approve = useCallback(
    async (altId) => {
      showLoading("Saving...");
      try {
        const response = await approveSpeedInAppAPI({ ALTID: altId, approved: true });

        if (!response?.success) {
          showError("Approve failed");
          hideLoading();
          return;
        }

        const { ALTID, Approved, ApprovedByName, ApprovedAt, RequiresApproval } = response.data;

        setSpeedApprovals((prev) =>
          prev.map((row) =>
            row.ALTID === ALTID
              ? { ...row, Approved, ApprovedByName, ApprovedAt, RequiresApproval }
              : row
          )
        );

        timeoutSuccess("Saved");
        hideLoading();
      } catch (err) {
        console.error(err);
        showError("Approve failed");
        hideLoading();
      }
    },
    [showError]
  );

  // Reject a speed
  const reject = useCallback(
    async (altId) => {
      showLoading("Saving...");
      try {
        const response = await approveSpeedInAppAPI({ ALTID: altId, approved: false });

        if (!response?.success) {
          showError("Reject failed");
          hideLoading();
          return;
        }

        setSpeedApprovals((prev) => prev.filter((row) => row.ALTID !== altId));

        timeoutSuccess("Saved");
        hideLoading();
      } catch (err) {
        console.error(err);
        showError("Reject failed");
        hideLoading();
      }
    },
    [showError]
  );

  return {
    speedApprovals,
    setSpeedApprovals,
    fetchSpeedApprovals,
    approve,
    reject
  };
};
